// ==========================================================================
// Video Thumbnail Loader Utility (Vimeo only)
// ==========================================================================

const VideoThumbnailLoader = {
  /**
   * Get thumbnail URL for Vimeo video
   * @param {string} videoId - Vimeo video ID
   * @returns {Promise<string>} Promise resolving to thumbnail URL
   */
  async getVimeoThumbnail(videoId) {
    try {
      const response = await fetch(`https://vimeo.com/api/v2/video/${videoId}.json`);
      const data = await response.json();
      // Return the largest thumbnail available
      return data[0].thumbnail_large || data[0].thumbnail_medium || data[0].thumbnail_small;
    } catch (error) {
      console.error('Error fetching Vimeo thumbnail:', error);
      return '';
    }
  },

  /**
   * Load thumbnail for a video element
   * @param {HTMLElement} element - Element with data-video-id
   */
  async loadThumbnail(element) {
    const videoId = element.getAttribute('data-video-id');

    if (!videoId) {
      console.warn('Missing video ID', element);
      return;
    }

    const thumbnailUrl = await this.getVimeoThumbnail(videoId);

    if (thumbnailUrl) {
      element.style.backgroundImage = `url('${thumbnailUrl}')`;
      element.style.backgroundSize = 'cover';
      element.style.backgroundPosition = 'center';
      element.style.backgroundRepeat = 'no-repeat';
      element.classList.add('video-thumbnail-loaded');
    }
  }
};

// ==========================================================================
// Video Player Manager (Vimeo only)
// ==========================================================================

const VideoPlayerManager = {
  // Store Vimeo player instances by element
  vimeoPlayers: new WeakMap(),

  /**
   * Create Vimeo player using VimeoPlayerComponent
   * @param {HTMLElement} container - Container element
   * @param {string} videoId - Vimeo video ID
   */
  createVimeoPlayer(container, videoId) {
    // Create a container for the Vimeo player component
    const playerContainer = document.createElement('div');
    playerContainer.classList.add('featured-video-player', 'featured-video-vimeo-player');
    container.appendChild(playerContainer);

    // Initialize VimeoPlayerComponent with autoplay
    if (window.VimeoPlayerComponent) {
      const vimeoPlayer = new window.VimeoPlayerComponent(playerContainer, {
        vimeoId: videoId,
        autoplay: true,
        muted: false,
        loop: false,
        showControls: true,
        posterUrl: null, // Don't show poster since we're auto-playing
        fillContainer: true // Fill the video container
      });

      // Store the player instance for cleanup
      this.vimeoPlayers.set(container, vimeoPlayer);
    } else {
      // Fallback to iframe if VimeoPlayerComponent is not available
      console.warn('VimeoPlayerComponent not available, falling back to iframe');
      const iframe = document.createElement('iframe');
      iframe.setAttribute('src', `https://player.vimeo.com/video/${videoId}?autoplay=1`);
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
      iframe.setAttribute('allowfullscreen', '');
      playerContainer.appendChild(iframe);
    }
  },

  /**
   * Play video in container
   * @param {HTMLElement} element - Video card element
   */
  playVideo(element) {
    const videoId = element.getAttribute('data-video-id');

    if (!videoId) return;

    // Add playing class
    element.classList.add('video-playing');

    // Remove existing player if any
    const existingPlayer = element.querySelector('.featured-video-player');
    if (existingPlayer) {
      // Destroy Vimeo player instance if exists
      const vimeoInstance = this.vimeoPlayers.get(element);
      if (vimeoInstance) {
        vimeoInstance.destroy();
        this.vimeoPlayers.delete(element);
      }
      existingPlayer.remove();
    }

    // Small delay to allow fade out animation
    setTimeout(() => {
      this.createVimeoPlayer(element, videoId);
    }, 150);
  },

  /**
   * Create Shopify video player
   * @param {HTMLElement} container - Container element
   * @param {string} videoSrc - Video source URL
   */
  createShopifyPlayer(container, videoSrc) {
    // Create a container for the video player
    const playerContainer = document.createElement('div');
    playerContainer.classList.add('featured-video-player', 'featured-video-shopify-player-wrapper');

    // Create video element
    const video = document.createElement('video');
    video.src = videoSrc;
    video.autoplay = true;
    video.controls = true;
    video.playsInline = true;
    video.muted = false;
    video.controlsList = "nodownload noplaybackrate";

    playerContainer.appendChild(video);
    container.appendChild(playerContainer);

    // Play the video
    video.play().catch(err => {
      console.warn('Shopify video autoplay failed:', err);
    });
  },

  /**
   * Play Shopify hosted video
   * @param {HTMLElement} element - Video card element with data-video-shopify-activator
   */
  playShopifyVideo(element) {
    const videoSrc = element.getAttribute('data-video-src');
    if (!videoSrc) return;

    // Add playing class
    element.classList.add('video-playing');

    // Remove existing player if any
    const existingPlayer = element.querySelector('.featured-video-player');
    if (existingPlayer) {
      existingPlayer.remove();
    }

    // Small delay to allow fade out animation (same as Vimeo)
    setTimeout(() => {
      this.createShopifyPlayer(element, videoSrc);
    }, 150);
  },

  /**
   * Stop Shopify hosted video
   * @param {HTMLElement} element - Video card element
   */
  stopShopifyVideo(element) {
    const player = element.querySelector('.featured-video-player');
    if (player) {
      // Fade out player
      player.style.animation = 'fadeOutPlayer 0.3s ease forwards';

      // Remove after animation
      setTimeout(() => {
        player.remove();
      }, 300);
    }
    element.classList.remove('video-playing');
  },

  /**
   * Stop video in container (handles both Vimeo and Shopify)
   * @param {HTMLElement} element - Video card element
   */
  stopVideo(element) {
    // Handle Shopify video
    if (element.hasAttribute('data-video-shopify-activator')) {
      this.stopShopifyVideo(element);
      return;
    }

    // Handle Vimeo player cleanup
    const vimeoInstance = this.vimeoPlayers.get(element);
    if (vimeoInstance) {
      vimeoInstance.destroy();
      this.vimeoPlayers.delete(element);
    }

    const player = element.querySelector('.featured-video-player');
    if (player) {
      // Fade out player
      player.style.animation = 'fadeOutPlayer 0.3s ease forwards';

      // Remove after animation
      setTimeout(() => {
        player.remove();
      }, 300);
    }
    element.classList.remove('video-playing');
  }
};

// ==========================================================================
// Featured Videos Slider
// ==========================================================================

class FeaturedVideosSlider {
  constructor(container) {
    this.container = container;
    this.slider = container.querySelector('.featured-videos__slider');

    if (!this.slider || typeof Splide === 'undefined') return;

    this.videoCount = parseInt(container.dataset.videoCount || '0');
    this.slidesPerPage = parseInt(container.dataset.slidesPerPage || '4');
    this.aspectRatio = container.dataset.aspectRatio || 'landscape';
    this.enableVideoModal = container.dataset.enableVideoModal === 'true';
    this.videoModalSize = container.dataset.videoModalSize || 'fullscreen';
    this.autoplayVideos = container.dataset.autoplayVideos === 'true';
    this.autoplayInstances = new Map(); // Track autoplay video instances
    this.init();
    this.loadVideoThumbnails();
    this.setupVideoClickHandlers();

    // Setup autoplay if enabled
    if (this.autoplayVideos) {
      this.setupAutoplay();
    }
  }

  // Determine if slider should be enabled
  shouldEnableSlider() {
    const isMobile = window.innerWidth < 1024;
    const hasEnoughVideos = this.videoCount >= 5;
    return isMobile || hasEnoughVideos;
  }

  // Calculate perPage for tablet breakpoint
  getTabletPerPage() {
    // For tablet, use slidesPerPage - 1, but minimum of 2
    return Math.max(2, this.slidesPerPage - 1);
  }

  // Determine if peek should be used (only for portrait and square)
  shouldUsePeek() {
    return this.aspectRatio === 'portrait' || this.aspectRatio === 'square';
  }

  init() {
    const tabletPerPage = this.getTabletPerPage();
    const usePeek = this.shouldUsePeek();

    // Initialize slider (always mounted, but may be disabled)
    this.splideInstance = window.SplideUtils.initSplideWithControls(
      this.slider,
      this.container,
      {
        // Splide configuration
        type: 'carousel',
        rewind: true,
        perPage: this.slidesPerPage,
        perMove: 1,
        gap: '1.5rem',
        padding: { left: 0, right: 0 },
        arrows: false, // Using custom arrows
        pagination: false,
        trimSpace: 'move',
        drag: this.shouldEnableSlider(), // Conditionally enable drag
        speed: 600,
        slideFocus: false, // Disable Splide's focus management
        breakpoints: {
          1024: {
            perPage: tabletPerPage,
            trimSpace: 'move',
          },
          767: {
            perPage: 2,
            gap: '1rem',
            trimSpace: 'move',
            padding: usePeek ? { right: '22%' } : { left: 0, right: 0 },
            drag: true, // Always enabled on mobile
          },
          576: {
            perPage: 1,
            gap: '1rem',
            padding: usePeek ? { right: '22%' } : { left: 0, right: 0 },
            trimSpace: 'move',
            drag: true, // Always enabled on mobile
          }
        },
        // Accessibility
        i18n: {
          prev: 'Previous video',
          next: 'Next video',
          first: 'Go to first video',
          last: 'Go to last video',
          slideX: 'Go to video %s',
          pageX: 'Go to page %s',
          play: 'Start autoplay',
          pause: 'Pause autoplay',
        },
      },
      {
        customArrows: true,
        scrollbar: true,
        tabindex: false
      }
    );

    // Remove tabindex from slider container and track elements
    if (this.splideInstance) {
      const sliderRoot = this.slider.querySelector('.splide__track');
      const sliderList = this.slider.querySelector('.splide__list');

      if (sliderRoot) sliderRoot.removeAttribute('tabindex');
      if (sliderList) sliderList.removeAttribute('tabindex');

      // Remove tabindex from slide elements (they don't need it, only the video cards inside do)
      const slides = this.slider.querySelectorAll('.splide__slide');
      slides.forEach(slide => slide.removeAttribute('tabindex'));
    }

    // Initial setup
    this.updateSlider();

    // Track current slide index for video stop logic
    this.lastKnownSlideIndex = this.splideInstance.index;

    // Use 'move' event (before animation) to capture the target index
    // and 'moved' event (after animation) to stop videos only if index changed
    this.splideInstance.on('move', (newIndex) => {
      // Only stop videos if we're actually moving to a different slide
      if (newIndex !== this.lastKnownSlideIndex) {
        this.stopAllVideos();
        this.lastKnownSlideIndex = newIndex;
      }
    });

    // Accessibility: Update tabindex when slides become visible/hidden
    this.splideInstance.on('mounted moved', () => {
      this.updateSlideAccessibility();
    });

    // Initial accessibility setup
    this.updateSlideAccessibility();

    // Handle resize
    this.resizeTimeout = null;
    this.handleResize = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.updateSlider(), 250);
    };
    window.addEventListener('resize', this.handleResize);
  }

  // Update slider state based on conditions
  updateSlider() {
    if (!this.splideInstance) return;

    const shouldEnable = this.shouldEnableSlider();

    if (shouldEnable) {
      this.container.classList.add('slider-active');
      // Enable drag on desktop
      this.splideInstance.options.drag = true;
      this.splideInstance.refresh();
    } else {
      this.container.classList.remove('slider-active');
      // Disable drag on desktop when not enough videos
      this.splideInstance.options.drag = false;
      // Go to first slide when disabling
      this.splideInstance.go(0);
      this.splideInstance.refresh();
    }
  }

  /**
   * Update tabindex on slide elements based on aria-hidden state
   * Prevents keyboard navigation to hidden slides
   */
  updateSlideAccessibility() {
    if (!this.splideInstance) return;

    const slides = this.slider.querySelectorAll('.splide__slide');

    slides.forEach(slide => {
      const isHidden = slide.getAttribute('aria-hidden') === 'true';
      const focusableElements = slide.querySelectorAll('[data-video-activator], [data-video-shopify-activator], a, button');

      focusableElements.forEach(element => {
        if (isHidden) {
          // Hidden slides: prevent keyboard focus
          element.setAttribute('tabindex', '-1');
        } else {
          // Visible slides: allow keyboard focus
          element.setAttribute('tabindex', '0');
        }
      });
    });
  }

  /**
   * Load thumbnails for all video elements in the slider
   */
  loadVideoThumbnails() {
    const videoElements = this.container.querySelectorAll('[data-video-thumbnail-loader]');
    videoElements.forEach(element => {
      VideoThumbnailLoader.loadThumbnail(element);
    });
  }

  /**
   * Stop all playing videos (both Vimeo and Shopify)
   */
  stopAllVideos() {
    // Stop Vimeo videos
    const vimeoCards = this.container.querySelectorAll('[data-video-activator]');
    vimeoCards.forEach(card => {
      VideoPlayerManager.stopVideo(card);
    });

    // Stop Shopify videos
    const shopifyCards = this.container.querySelectorAll('[data-video-shopify-activator]');
    shopifyCards.forEach(card => {
      VideoPlayerManager.stopVideo(card);
    });
  }

  /**
   * Setup click handlers for video cards (both Vimeo and Shopify)
   */
  setupVideoClickHandlers() {
    // Get all video cards (both Vimeo and Shopify)
    const vimeoCards = this.container.querySelectorAll('[data-video-activator]');
    const shopifyCards = this.container.querySelectorAll('[data-video-shopify-activator]');
    const allVideoCards = [...vimeoCards, ...shopifyCards];

    // Track if last interaction was keyboard (for focus handling)
    let lastInteractionWasKeyboard = false;

    // Detect keyboard vs mouse interaction
    this.container.addEventListener('mousedown', () => {
      lastInteractionWasKeyboard = false;
    });

    this.container.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        lastInteractionWasKeyboard = true;
      }
    });

    allVideoCards.forEach((card, index) => {
      // Track drag vs click
      let startX = 0;
      let startY = 0;
      let isDrag = false;

      card.addEventListener('mousedown', (e) => {
        // Don't track for player areas
        if (e.target.closest('.vimeo-player') || e.target.closest('.featured-video-vimeo-player') || e.target.closest('.featured-video-shopify-player')) {
          return;
        }
        startX = e.clientX;
        startY = e.clientY;
        isDrag = false;
      });

      card.addEventListener('mousemove', (e) => {
        // If mouse moved more than 10px, consider it a drag
        if (Math.abs(e.clientX - startX) > 10 || Math.abs(e.clientY - startY) > 10) {
          isDrag = true;
        }
      });

      // Click handler
      card.addEventListener('click', (e) => {
        // Don't interfere with clicks on video player controls
        if (e.target.closest('.vimeo-player') || e.target.closest('.featured-video-vimeo-player') || e.target.closest('.featured-video-shopify-player')) {
          return;
        }

        // If it was a drag (slider swipe), don't toggle video
        if (isDrag) {
          isDrag = false;
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        const isShopifyVideo = card.hasAttribute('data-video-shopify-activator');

        // If modal playback is enabled, open in global VideoModal
        if (this.enableVideoModal && window.VideoModal) {
          const vimeoId = card.dataset.modalVimeoId;
          const shopifyVideoSrc = card.dataset.modalShopifyVideoSrc;

          if (vimeoId || shopifyVideoSrc) {
            window.VideoModal.open({
              type: vimeoId ? 'vimeo' : 'shopify',
              vimeoId: vimeoId,
              shopifyVideoSrc: shopifyVideoSrc,
              size: this.videoModalSize,
              triggerElement: card
            });
          }
          return;
        }

        // Inline playback (default behavior)
        // Don't stop video if already playing - let it continue playing
        if (!card.classList.contains('video-playing')) {
          // If autoplay is active, stop it first
          if (this.autoplayVideos && card.classList.contains('video-autoplaying')) {
            this.stopAutoplay(card);
          }

          // Stop any other playing videos (both Vimeo and Shopify)
          this.stopAllVideos();

          // Move slider to this video
          if (this.splideInstance) {
            this.splideInstance.go(index);
          }

          // Play this video (use appropriate method based on type)
          if (isShopifyVideo) {
            VideoPlayerManager.playShopifyVideo(card);
          } else {
            VideoPlayerManager.playVideo(card);
          }

          // Setup focus loss handler to reset video
          this.setupVideoResetOnBlur(card);
        }
      }, true);

      // Keyboard accessibility
      // Note: tabindex is managed dynamically by updateSlideAccessibility()
      // based on slide visibility (aria-hidden state)
      card.setAttribute('role', 'button');
      card.setAttribute('aria-label', `Play featured video ${index + 1}`);

      // Focus handler - only move slider for keyboard navigation
      card.addEventListener('focus', () => {
        if (lastInteractionWasKeyboard && this.splideInstance) {
          this.splideInstance.go(index);
        }
      });

      // Enter/Space to activate
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          card.click();
        }
      });
    });
  }

  /**
   * Setup handler to reset video when focus is lost
   * @param {HTMLElement} card - Video card element
   */
  setupVideoResetOnBlur(card) {
    // Use a slight delay to check if focus moved outside the video player
    const checkFocusLoss = () => {
      setTimeout(() => {
        const playerContainer = card.querySelector('.featured-video-player');
        if (!playerContainer) return;

        // Check if the currently focused element is outside the player container
        const focusedElement = document.activeElement;
        const isFocusInPlayer = playerContainer.contains(focusedElement);
        const isFocusOnCard = card.contains(focusedElement) && focusedElement === card;

        // If focus is not in the player and not on the card itself, reset the video
        if (!isFocusInPlayer && !isFocusOnCard) {
          VideoPlayerManager.stopVideo(card);
        }
      }, 100);
    };

    // Listen for focusout on the card
    card.addEventListener('focusout', checkFocusLoss);

    // Also listen for clicks outside the player
    const handleClickOutside = (e) => {
      const playerContainer = card.querySelector('.featured-video-player');
      if (!playerContainer) return;

      // If click is outside the card, stop the video
      if (!card.contains(e.target)) {
        VideoPlayerManager.stopVideo(card);
        // Remove this listener once video is stopped
        document.removeEventListener('click', handleClickOutside);
      }
    };

    // Add click outside listener after a brief delay to avoid immediate trigger
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);
  }

  /**
   * Setup autoplay for videos (muted, looping)
   */
  setupAutoplay() {
    const vimeoCards = this.container.querySelectorAll('[data-video-activator]');
    const shopifyCards = this.container.querySelectorAll('[data-video-shopify-activator]');
    const allVideoCards = [...vimeoCards, ...shopifyCards];

    // Setup intersection observer to autoplay videos when visible
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5 // Video must be 50% visible
    };

    this.autoplayObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const card = entry.target;
        const isShopifyVideo = card.hasAttribute('data-video-shopify-activator');

        if (entry.isIntersecting) {
          // Start autoplay when visible
          if (isShopifyVideo) {
            this.startShopifyAutoplay(card);
          } else {
            this.startVimeoAutoplay(card);
          }
        } else {
          // Pause autoplay when not visible
          this.stopAutoplay(card);
        }
      });
    }, observerOptions);

    // Observe all video cards
    allVideoCards.forEach(card => {
      this.autoplayObserver.observe(card);
    });
  }

  /**
   * Start Vimeo autoplay (muted, looping)
   * @param {HTMLElement} card - Video card element
   */
  startVimeoAutoplay(card) {
    // Skip if already has autoplay instance or user is playing unmuted
    if (this.autoplayInstances.has(card) || card.classList.contains('video-playing')) {
      return;
    }

    const videoId = card.getAttribute('data-video-id');
    if (!videoId) return;

    // Create autoplay player container
    const playerContainer = document.createElement('div');
    playerContainer.classList.add('featured-video-player', 'featured-video-vimeo-player', 'featured-video-autoplay');
    card.appendChild(playerContainer);

    // Initialize VimeoPlayerComponent with autoplay, muted, loop
    if (window.VimeoPlayerComponent) {
      const vimeoPlayer = new window.VimeoPlayerComponent(playerContainer, {
        vimeoId: videoId,
        autoplay: true,
        muted: true,
        loop: true,
        showControls: false, // Hide controls for autoplay
        posterUrl: null,
        fillContainer: true
      });

      this.autoplayInstances.set(card, { type: 'vimeo', player: vimeoPlayer });
      card.classList.add('video-autoplaying');
    }
  }

  /**
   * Start Shopify video autoplay (muted, looping)
   * @param {HTMLElement} card - Video card element
   */
  startShopifyAutoplay(card) {
    // Skip if already has autoplay instance or user is playing unmuted
    if (this.autoplayInstances.has(card) || card.classList.contains('video-playing')) {
      return;
    }

    const videoSrc = card.getAttribute('data-video-src');
    if (!videoSrc) return;

    // Create autoplay player container
    const playerContainer = document.createElement('div');
    playerContainer.classList.add('featured-video-player', 'featured-video-shopify-player-wrapper', 'featured-video-autoplay');

    // Create video element
    const video = document.createElement('video');
    video.src = videoSrc;
    video.autoplay = true;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false; // Hide controls for autoplay

    playerContainer.appendChild(video);
    card.appendChild(playerContainer);

    // Play the video
    video.play().catch(err => {
      console.warn('Shopify video autoplay failed:', err);
    });

    this.autoplayInstances.set(card, { type: 'shopify', player: video, container: playerContainer });
    card.classList.add('video-autoplaying');
  }

  /**
   * Stop autoplay for a video card
   * @param {HTMLElement} card - Video card element
   */
  stopAutoplay(card) {
    const instance = this.autoplayInstances.get(card);
    if (!instance) return;

    if (instance.type === 'vimeo' && instance.player) {
      instance.player.destroy();
    } else if (instance.type === 'shopify' && instance.player) {
      instance.player.pause();
      instance.player.currentTime = 0;
    }

    // Remove autoplay player elements
    const autoplayPlayers = card.querySelectorAll('.featured-video-autoplay');
    autoplayPlayers.forEach(player => player.remove());

    this.autoplayInstances.delete(card);
    card.classList.remove('video-autoplaying');
  }

  /**
   * Stop all autoplay videos
   */
  stopAllAutoplay() {
    this.autoplayInstances.forEach((instance, card) => {
      this.stopAutoplay(card);
    });
  }

  destroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.splideInstance) {
      this.splideInstance.destroy();
    }

    // Clean up autoplay
    if (this.autoplayVideos) {
      this.stopAllAutoplay();
      if (this.autoplayObserver) {
        this.autoplayObserver.disconnect();
      }
    }

    // Clean up video players
    this.stopAllVideos();
  }
}

// ==========================================================================
// Initialize all featured videos sliders on the page
// ==========================================================================

document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll('[id^="FeaturedVideos-"]');
  containers.forEach(container => {
    const slider = container.querySelector('.featured-videos__slider');
    if (slider) {
      container.sliderInstance = new FeaturedVideosSlider(container);
    }
  });
});

// ==========================================================================
// Shopify Theme Editor Support
// ==========================================================================

if (typeof window.SplideUtils !== 'undefined') {
  window.SplideUtils.setupThemeEditorEvents(
    'FeaturedVideos-',
    '.featured-videos__slider',
    (sectionElement) => {
      const container = sectionElement.querySelector('[id^="FeaturedVideos-"]');
      if (container) {
        const slider = container.querySelector('.featured-videos__slider');
        if (slider) {
          if (container.sliderInstance) {
            container.sliderInstance.destroy();
          }
          container.sliderInstance = new FeaturedVideosSlider(container);
        }
      }
    }
  );
}
